import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../services/adminApi";
import usePolling from "../hooks/usePolling";
import Pagination from "../components/Pagination";

export default function TradeManagement() {
  const [filters, setFilters] = useState({ userName: "", tradeId: "", from: "", to: "" });
  const [data, setData] = useState({ items: [], summary: {}, total: 0, page: 1, pageSize: 10 });
  const [error, setError] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [actionLoadingTradeId, setActionLoadingTradeId] = useState("");

  const load = useCallback(async () => {
    try {
      const next = await adminApi.getTrades({
        page: data.page,
        pageSize: data.pageSize,
        ...filters,
      });
      setData(next);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load trades");
    }
  }, [filters, data.page, data.pageSize]);

  usePolling(load, 20000, [
    filters.userName,
    filters.tradeId,
    filters.from,
    filters.to,
    data.page,
    data.pageSize,
  ]);

  useEffect(() => {
    setData((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [filters.userName, filters.tradeId, filters.from, filters.to]);

  const loadUserProfile = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const details = await adminApi.getUserDetails(userId);
      setUserProfile(details);
      setSelectedUserId(userId);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load user profile");
    }
  }, []);

  const handleAdminCloseTrade = async (tradeId) => {
    if (!tradeId) return;
    try {
      setActionLoadingTradeId(tradeId);
      await adminApi.closeTrade(tradeId);
      await load();
      if (selectedUserId) {
        await loadUserProfile(selectedUserId);
      }
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to close trade");
    } finally {
      setActionLoadingTradeId("");
    }
  };

  return (
    <div>
      <h2>Trade Management</h2>
      <div className="row" style={{ marginBottom: 12 }}>
        <input
          placeholder="User name/email"
          value={filters.userName}
          onChange={(e) => setFilters((p) => ({ ...p, userName: e.target.value }))}
        />
        <input
          placeholder="Trade ID"
          value={filters.tradeId}
          onChange={(e) => setFilters((p) => ({ ...p, tradeId: e.target.value }))}
        />
        <input type="date" value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} />
        <input type="date" value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} />
        <button className="btn-primary" onClick={load}>Refresh</button>
      </div>
      {error ? <div className="error">{error}</div> : null}

      <div className="row" style={{ marginBottom: 12 }}>
        <div className="card">Successful Trades: {data.summary?.totalSuccessfulTrades || 0}</div>
        <div className="card">Trade Earnings: {(data.summary?.totalTradeEarnings || 0).toFixed(2)}</div>
        <div className="card">License Purchased Users: {data.summary?.usersWithLicense || 0}</div>
        <div className="card">No License Users: {data.summary?.usersWithoutLicense || 0}</div>
      </div>

      {userProfile?.profile ? (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ marginTop: 0 }}>User Profile</h3>
          <div className="row">
            <div>
              <strong>Name:</strong> {userProfile.profile.name || "-"}
            </div>
            <div>
              <strong>Email:</strong> {userProfile.profile.email || "-"}
            </div>
            <div>
              <strong>Active Trades:</strong> {userProfile.activeTrades?.length || 0}
            </div>
            <div>
              <strong>Total Earnings:</strong> {(userProfile.summary?.totalEarnings || 0).toFixed(2)}
            </div>
            <div>
              <strong>Total Profit:</strong> {(userProfile.summary?.totalProfit || 0).toFixed(2)}
            </div>
            <div>
              <strong>Total Revenue:</strong> {(userProfile.summary?.totalRevenue || 0).toFixed(2)}
            </div>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Trade ID</th>
                <th>User</th>
                <th>Pair</th>
                <th>Investment</th>
                <th>Status</th>
                <th>Profit</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>
                    <button
                      className="btn-secondary"
                      onClick={() => loadUserProfile(item.user?.id || item.userId)}
                    >
                      {item.user?.email || item.userId}
                    </button>
                  </td>
                  <td>{item.pair}</td>
                  <td>{item.investment}</td>
                  <td>{item.status}</td>
                  <td>{item.adminProfit || 0}</td>
                  <td>{new Date(item.createdAt).toLocaleString()}</td>
                  <td>
                    {item.status === "pending" || item.status === "executed" ? (
                      <button
                        className="btn-primary"
                        disabled={actionLoadingTradeId === item.id}
                        onClick={() => handleAdminCloseTrade(item.id)}
                      >
                        {actionLoadingTradeId === item.id ? "Closing..." : "Close Trade"}
                      </button>
                    ) : (
                      <span>Closed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          total={data.total}
          onPageChange={(nextPage) =>
            setData((prev) => ({
              ...prev,
              page: nextPage,
            }))
          }
        />
      </div>
    </div>
  );
}
